export {MyPlaylists as Page};

import {Helmet} from "react-helmet-async";
import {useEffect, useState, useContext} from "preact/hooks";
import {AccountSidebar} from "../../../components/AccountSidebar.jsx";
import {ContentLocked} from "../../../components/ContentLocked.jsx";
import messages from "../../../messages/messages.js";
import API_URL from "../../../includes/api.js";
import {Playlist} from "../../../components/Playlist.jsx";
import {useLocation} from "../../../hooks/useLocation.js";
import {TrashIcon} from "@heroicons/react/24/outline/index.js";
import {NotificationContext} from '../../../components/NotificationContext.jsx';
import {useAuth} from "../../../components/AuthProvider.jsx";

function MyPlaylists() {
    const location = useLocation();
    const {addNotification} = useContext(NotificationContext);
    const {getToken, user, authLoading} = useAuth();

    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState('');
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(null);

    useEffect(() => {
        if (authLoading) return;

        if (!user) return;

        getToken().then(token => fetchPlaylists(token));
    }, [authLoading, user])

    const fetchPlaylists = (token) => {
        setLoading(true);
        fetch(`${API_URL}/playlists/list?firebase_auth_token=${token}&preview=true`)
            .then(response => response.json()).then(data => {
            if (data.code === 200) {
                setPlaylists(data.data);
            } else {
                addNotification(data.message, 'error');
            }
        }).catch(() => {
            addNotification('Failed to load playlists', 'error');
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        const token = await getToken();
        const formData = new FormData();
        formData.append('firebase_auth_token', token);
        formData.append('title', newName.trim());
        fetch(`${API_URL}/playlists/create`, {
            method: 'POST',
            body: formData
        }).then(res => res.json()).then(data => {
            if (data.code === 200) {
                setNewName('');
                fetchPlaylists(token);
            } else {
                addNotification(data.message, 'error');
            }
        }).catch(() => {
            addNotification('Could not create playlist.', 'error');
        }).finally(() => {
            setCreating(false)
        });
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this playlist? This action cannot be undone.')) {
            return;
        }
        setDeleting(id);
        const token = await getToken();
        const formData = new FormData();
        formData.append('firebase_auth_token', token);
        formData.append('playlist_id', id);
        fetch(`${API_URL}/playlists/delete`, {
            method: 'POST',
            body: formData
        })
            .then(res => res.json()).then(data => {
            if (data.code === 200) {
                fetchPlaylists(token);
            } else {
                addNotification(data.message, 'error');
            }
        }).catch(() => {
            addNotification('Could not delete playlist.', 'error');
        }).finally(() => {
            setDeleting(null)
        });
    };

    return (<section className="text-white">

        <Helmet>
            <title>{messages["SITE.NAME"]} | My subscriber website</title>
            <meta name="description" content="My subscriber website"/>
        </Helmet>

        <div>

            <div className="flex flex-wrap">

                <AccountSidebar/>

                <div className="w-full md:w-3/4 px-0 md:px-4">

                    <div className="p-4 text-yellow-800 rounded-lg bg-yellow-50 mb-4">
                        <h3 className="text-lg m-0 text-yellow-800 leading-none">Free Beta Access</h3>
                        <div className="text-sm mt-2">
                            Beta users get unlimited custom playlists. When we launch you will be able to keep your
                            playlists, but not create new ones unless you subscribe.
                        </div>
                    </div>

                    <div className="border border-border/60 bg-zinc-800 rounded-lg p-6 mb-3">

                        <h2 className="text-2xl font-bold mb-4">My Playlists</h2>
                        <p className="text-sm text-gray-400">
                            Here you can create and manage your custom playlists. Playlists allow you to group songs
                            together for easy access and organization.
                        </p>

                        <form onSubmit={handleCreate} className="flex gap-2 mb-6 max-w-md">
                            <input
                                type="text"
                                aria-label="New playlist name"
                                value={newName}
                                onChange={e => setNewName(e.target.value)}
                                placeholder="New playlist name"
                                className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                disabled={creating}
                            />
                            <button
                                type="submit"
                                className="px-10 cursor-pointer text-white border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm py-2.5 text-center"
                                disabled={creating}
                            >
                                {creating ? 'Creating...' : 'Create'}
                            </button>
                        </form>

                        {loading ? (
                            <div className="text-gray-400">Loading playlists...</div>
                        ) : playlists.length === 0 ? (
                            <div className="text-gray-400">No playlists found.</div>
                        ) : (
                            <div className="grid text-center grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                                {playlists.map(pl => (
                                    <div className="relative" key={pl.id}>
                                        <Playlist
                                            playlist={{
                                                id: "custom-" + pl.id,
                                                category_name: pl.title,
                                                youtube_url: pl.preview_song?.youtube_url,
                                                full_view: false
                                            }}
                                            location={location}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(pl.id)}
                                            title="Delete playlist"
                                            aria-label={`Delete playlist ${pl.title}`}
                                            disabled={deleting === pl.id}
                                            className="z-1 absolute right-3 top-3 rounded-sm bg-black/20 p-1 cursor-pointer disabled:opacity-60"
                                        >
                                            <TrashIcon className="w-6 h-6" aria-hidden="true"/>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </section>);
}
